import { AlbumMediaUploader, Media, MediaIndex, MediaUploaderFilter, MediaDuplicateFingerprints, MultipleMediaUploadResult, MediaLike, MediaComment, CreateMediaCommentDto, UpdateMediaCommentDto } from "../types/media";
import { makeAuthenticatedRequest, makeAuthenticatedUploadRequest } from "./tokenHelper";
import { DEFAULT_MEDIA_SORT, MediaSortOptions } from "../types/mediaSort";
import { getUploadDeviceInfo } from "../utils/uploadDeviceInfo";
import { albumDebugDuration } from "../utils/albumDebug";

export interface MediaUploadProgress {
  totalBytes: number;
  uploadedBytes: number;
  totalFiles: number;
  currentFileIndex: number; // 0-based
  currentFileName?: string;
  currentFileBytesUploaded?: number;
  currentFileBytesTotal?: number;
}

class MediaService {

  async listMedia(
    ownerId: string,
    albumId: string,
    offset: number = 0,
    limit: number = 10,
    sort: MediaSortOptions = DEFAULT_MEDIA_SORT,
    uploaderFilter?: MediaUploaderFilter | null
  ): Promise<Media[]> {
    const params = new URLSearchParams({
      offset: String(offset),
      limit: String(limit),
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
    });
    for (const source of uploaderFilter ?? []) {
      params.append("source", source);
    }

    const startedAt = performance.now();
    const result = await makeAuthenticatedRequest<Media[]>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media?${params.toString()}`
    );
    albumDebugDuration("api.listMedia", startedAt, {
      albumId,
      offset,
      limit,
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
      returned: result.length,
    });
    return result;
  }

  async listMediaIndex(
    ownerId: string,
    albumId: string,
    sort: MediaSortOptions = DEFAULT_MEDIA_SORT,
    uploaderFilter?: MediaUploaderFilter | null
  ): Promise<MediaIndex> {
    const params = new URLSearchParams({
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
    });
    for (const source of uploaderFilter ?? []) {
      params.append("source", source);
    }

    const startedAt = performance.now();
    const result = await makeAuthenticatedRequest<MediaIndex>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media/index?${params.toString()}`
    );
    albumDebugDuration("api.listMediaIndex", startedAt, {
      albumId,
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
      items: result.items.length,
    });
    return result;
  }

  async listDuplicateFingerprints(ownerId: string, albumId: string): Promise<MediaDuplicateFingerprints> {
    return makeAuthenticatedRequest<MediaDuplicateFingerprints>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media/duplicate-fingerprints`
    );
  }

  async countMedia(
    ownerId: string,
    albumId: string,
    uploaderFilter?: MediaUploaderFilter | null
  ): Promise<number> {
    const params = new URLSearchParams();
    for (const source of uploaderFilter ?? []) {
      params.append("source", source);
    }
    const query = params.toString();
    const result = await makeAuthenticatedRequest<{ total: number }>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media/count${query ? `?${query}` : ""}`
    );
    return result.total;
  }

  async listAlbumUploaders(ownerId: string, albumId: string): Promise<AlbumMediaUploader[]> {
    return makeAuthenticatedRequest<AlbumMediaUploader[]>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media/uploaders`
    );
  }

  async getMedia(ownerId: string, albumId: string, mediaId: string): Promise<Media> {
    return makeAuthenticatedRequest<Media>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media/${encodeURIComponent(mediaId)}`
    );
  }

  async uploadMultipleMedia(
    ownerId: string,
    albumId: string,
    files: File[],
    onProgress?: (progress: MediaUploadProgress) => void
  ): Promise<MultipleMediaUploadResult> {
    const deviceInfo = await getUploadDeviceInfo();
    const totalFiles = files.length;
    const totalBytes = files.reduce((sum, f) => sum + (Number.isFinite(f.size) ? f.size : 0), 0);

    const successful: Media[] = [];
    const failed: { filename: string; error: string }[] = [];

    let completedBytes = 0;

    // Upload sequentially so progress can be attributed to a specific file and weighted by file size.
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const currentFileTotal = Number.isFinite(file.size) ? file.size : 0;

      // Emit a "starting file" progress tick
      onProgress?.({
        totalBytes,
        uploadedBytes: completedBytes,
        totalFiles,
        currentFileIndex: i,
        currentFileName: file.name,
        currentFileBytesUploaded: 0,
        currentFileBytesTotal: currentFileTotal,
      });

      const formData = new FormData();
      formData.append("files", file);
      if (deviceInfo.uploadDeviceModel) formData.append("uploadDeviceModel", deviceInfo.uploadDeviceModel);
      if (deviceInfo.uploadDevicePlatform) formData.append("uploadDevicePlatform", deviceInfo.uploadDevicePlatform);
      if (deviceInfo.uploadDevicePlatformVersion) {
        formData.append("uploadDevicePlatformVersion", deviceInfo.uploadDevicePlatformVersion);
      }

      try {
        const result = await makeAuthenticatedUploadRequest<MultipleMediaUploadResult>(
          `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media`,
          formData,
          ({ loaded }) => {
            // Map XHR's multipart progress to just the file's byte size (ignoring multipart overhead).
            const currentLoaded = Math.min(Math.max(loaded, 0), currentFileTotal);
            onProgress?.({
              totalBytes,
              uploadedBytes: completedBytes + currentLoaded,
              totalFiles,
              currentFileIndex: i,
              currentFileName: file.name,
              currentFileBytesUploaded: currentLoaded,
              currentFileBytesTotal: currentFileTotal,
            });
          }
        );

        successful.push(...(result.successful || []));
        failed.push(...(result.failed || []));
      } catch (error) {
        const message = error instanceof Error ? error.message : "Upload failed";
        failed.push({ filename: file.name, error: message });
      } finally {
        completedBytes += currentFileTotal;
        onProgress?.({
          totalBytes,
          uploadedBytes: completedBytes,
          totalFiles,
          currentFileIndex: Math.min(i + 1, Math.max(totalFiles - 1, 0)),
          currentFileName: i + 1 < totalFiles ? files[i + 1].name : file.name,
          currentFileBytesUploaded: i + 1 < totalFiles ? 0 : currentFileTotal,
          currentFileBytesTotal: i + 1 < totalFiles ? (Number.isFinite(files[i + 1].size) ? files[i + 1].size : 0) : currentFileTotal,
        });
      }
    }

    // Final tick
    onProgress?.({
      totalBytes,
      uploadedBytes: totalBytes,
      totalFiles,
      currentFileIndex: Math.max(totalFiles - 1, 0),
      currentFileName: files[files.length - 1]?.name,
      currentFileBytesUploaded: files[files.length - 1]?.size ?? 0,
      currentFileBytesTotal: files[files.length - 1]?.size ?? 0,
    });

    return {
      successful,
      failed,
      totalUploaded: successful.length,
      totalFailed: failed.length,
    };
  }

  async deleteMedia(ownerId: string, albumId: string, mediaId: string): Promise<Media> {
    return makeAuthenticatedRequest<Media>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media/${encodeURIComponent(mediaId)}`,
      {
        method: "DELETE",
      }
    );
  }

  async getMediaLikes(ownerId: string, albumId: string, mediaId: string, offset: number = 0, limit: number = 10): Promise<MediaLike[]> {
    return makeAuthenticatedRequest<MediaLike[]>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media/${encodeURIComponent(mediaId)}/likes?offset=${offset}&limit=${limit}`
    );
  }

  async createMediaLike(ownerId: string, albumId: string, mediaId: string): Promise<MediaLike> {
    return makeAuthenticatedRequest<MediaLike>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media/${encodeURIComponent(mediaId)}/likes`,
      {
        method: "POST",
      }
    );
  }

  async deleteMediaLike(ownerId: string, albumId: string, mediaId: string, likeId: string): Promise<void> {
    return makeAuthenticatedRequest<void>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media/${encodeURIComponent(mediaId)}/likes/${encodeURIComponent(likeId)}`,
      {
        method: "DELETE",
      }
    );
  }

  async getMediaComments(ownerId: string, albumId: string, mediaId: string, offset: number = 0, limit: number = 10): Promise<MediaComment[]> {
    return makeAuthenticatedRequest<MediaComment[]>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media/${encodeURIComponent(mediaId)}/comments?offset=${offset}&limit=${limit}`
    );
  }

  async createMediaComment(ownerId: string, albumId: string, mediaId: string, createMediaCommentDto: CreateMediaCommentDto): Promise<MediaComment> {
    return makeAuthenticatedRequest<MediaComment>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media/${encodeURIComponent(mediaId)}/comments`,
      {
        method: "POST",
        body: JSON.stringify(createMediaCommentDto),
      }
    );
  }

  async updateMediaComment(ownerId: string, albumId: string, mediaId: string, commentId: string, updateMediaCommentDto: UpdateMediaCommentDto): Promise<MediaComment> {
    return makeAuthenticatedRequest<MediaComment>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media/${encodeURIComponent(mediaId)}/comments/${encodeURIComponent(commentId)}`,
      {
        method: "PUT",
        body: JSON.stringify(updateMediaCommentDto),
      }
    );
  }

  async deleteMediaComment(ownerId: string, albumId: string, mediaId: string, commentId: string): Promise<void> {
    return makeAuthenticatedRequest<void>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/media/${encodeURIComponent(mediaId)}/comments/${encodeURIComponent(commentId)}`,
      {
        method: "DELETE",
      }
    );
  }
}

export const mediaService = new MediaService();
